import { DgtPdOrderReasonCode, DgtPdProductionOrder } from '../models';
import { ApiResponse, post } from './utils'
import { Auth, API } from 'aws-amplify'
import { ProductionOrder } from '../components/types'
import {
  getDgtPdProductionOrder
} from '../graphql/queries'
import {
  updateDgtPdProductionOrder,
} from '../graphql/mutations'

import { handleOrderTags, handleRemoveOrderTags } from '../pages/Form/WorkCenter/logic'
import { OrderStatusCode } from '../constants/Enum';

export const syncOrders = async (orderIDs: string[]): Promise<ApiResponse> => {
  try {
    const path = '/sap/order/sync';
    const result = await post(path, {
      "OrderIDs": orderIDs
    })
    return {
      success: true,
      message: result.Message
    }
  } catch (error: any) {
    return {
      success: false,
      message: error.response ? error.response.data.message : 'Error from API, Please contact administrator.'
    }
  }
}

export interface ApiResponseWithCompoents extends ApiResponse{
  new_components: DgtPdOrderReasonCode[]
}

export const syncReason = async (orderIDs: string[]): Promise<ApiResponseWithCompoents> => {
  try {
    const path = '/sap/reasoncode/sync';
    const result = await post(path, {
      "OrderIDs": orderIDs
    })
    return {
      success: true,
      message: result.Message,
      new_components: result.new_components
    }
  } catch (error: any) {
    return {
      success: false,
      message: error.response ? error.response.data.message : 'Error from API, Please contact administrator.',
      new_components: []
    }
  }
}
interface SyncAmountComponent {
  materialCode: string
  materialDescription: string
  materialType: string
  materialRequestQty?: number | null
}
export const syncPicklistOrder = async (
  orderID: string, division: string, components: SyncAmountComponent[], mvKeys: string[],
  isExtra? : boolean): Promise<ApiResponse> => {
  try {
    const path = '/sap/picklist/sync';
    const payload = {
      "order_id": orderID,
      "division": division,
      "components": components,
      "is_extra": isExtra && isExtra === true ? true : false,
      "movement_keys": mvKeys
    }
    const result = await post(path, payload)
    return {
      success: true,
      message: result.message
    }
  } catch (error: any) {
    return {
      success: false,
      message: error.response ? error.response.data.message : 'Error from API, Please contact administrator.'
    }
  }
}

export const verifyMixingUser = async (
  employeeId: string, password: string): Promise<ApiResponse> => {
  try {
    const user = await Auth.currentUserPoolUser()
    const path = '/user/verify';
    const payload = {
      "username": employeeId,
      "password": password,
      "clientId": user.pool.clientId
    }
    const result = await post(path, payload)
    return {
      success: result.verify,
      message: result.message,
      name: result.name
    }
  } catch (error: any) {
    return {
      success: false,
      message: error.response ? error.response.data.message : 'Error from API, Please contact administrator.'
    }
  }
}

export const getOrder = async (orderId : string, customQuery?: string) : Promise<ProductionOrder> => {
  const getResult: any = await API.graphql({
    query: customQuery || getDgtPdProductionOrder,
    variables: { id: orderId }
  })
  return getResult.data?.getDgtPdProductionOrder
}

export const updateActualBatchNo = async (orderId : string, batchNos : string[])
: Promise<ProductionOrder> => {
  const order = await getOrder(orderId)
  if (order) {
    const updateResult : any = await API.graphql({
      query: updateDgtPdProductionOrder,
      variables: { input: {
        _version: order._version, 
        id: orderId,
        actualBatchNumbers: JSON.stringify(batchNos)
      }}
    })
    if (updateResult.data) return updateResult.data.updateDgtPdProductionOrder
  }
  throw Error('not found')
}

export const updateDivision = async (orderId : string, division : string) => {
  const order = await getOrder(orderId)
  if (order) {
    const updateResult : any = await API.graphql({
      query: updateDgtPdProductionOrder,
      variables: { input: {
        _version: order._version, 
        id: orderId,
        division
      }}
    })
    if (updateResult.data) return updateResult.data.updateDgtPdProductionOrder
  }
  throw Error('not found')
}

export const updateStatus = async (orderId : string, status : string) => {
  const order = await getOrder(orderId)
  if (order) {
    const updateResult : any = await API.graphql({
      query: updateDgtPdProductionOrder,
      variables: { input: {
        _version: order._version, 
        id: orderId,
        statusFromApp: status
      }}
    })
    if (updateResult.data) return updateResult.data.updateDgtPdProductionOrder
  }
  throw Error('not found')
}

export const updateTags = async (orderId : string, tags : string) => {
  const order = await getOrder(orderId)
  if (order) {
    const orderTags = handleOrderTags(order, tags)
    if(orderTags.status){
      // check Duplicate
      if(orderTags.isDuplicateTag) return order
      const updateResult : any = await API.graphql({
        query: updateDgtPdProductionOrder,
        variables: { input: {
          _version: order._version, 
          id: orderId,
          tags: JSON.stringify(orderTags.tags)
        }}
      })
      if (updateResult.data) return updateResult.data.updateDgtPdProductionOrder
    }
  }
  throw Error('not found')
}

export const removeTags = async (orderId : string, tags : string) => {
  const order = await getOrder(orderId)
  if (order) {
    const orderTags = handleRemoveOrderTags(order, tags)
    if(orderTags){
      const updateResult : any = await API.graphql({
        query: updateDgtPdProductionOrder,
        variables: { input: {
          _version: order._version, 
          id: orderId,
          tags: JSON.stringify(orderTags)
        }}
      })
      if (updateResult.data) return updateResult.data.updateDgtPdProductionOrder
    }
  }
  throw Error('not found')
}

export const updateFGInspectionForm = async (orderId : string, forms : string[]) : Promise<DgtPdProductionOrder> => {
  const order = await getOrder(orderId)
  if (order) {
    const updateResult : any = await API.graphql({
      query: updateDgtPdProductionOrder,
      variables: { input: {
        _version: order._version, 
        id: orderId,
        fgInspectionTypes: JSON.stringify(forms)
      }}
    })
    if (updateResult.data) return updateResult.data.updateDgtPdProductionOrder
  }
  throw Error('not found')
}

export const updateDraftDirectly = async (orderId : string, currentVersion: string)
: Promise<ProductionOrder> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdProductionOrder,
    variables: { input: {
      _version: currentVersion, 
      id: orderId,
      statusFromApp: OrderStatusCode.DRAFTED
    }}
  })
  return updateResult.data.updateDgtPdProductionOrder
}

export const updateInProgressDirectly = async (orderId : string, currentVersion: string, actualDueDate: string)
: Promise<ProductionOrder> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdProductionOrder,
    variables: { input: {
      _version: currentVersion, 
      id: orderId,
      statusFromApp: OrderStatusCode.IN_PROGRESS,
      actualDueDate
    }}
  })
  return updateResult.data.updateDgtPdProductionOrder
}

export const updateCountEditWorkCenterDirectly = async (orderId : string, currentVersion: string, count: number)
: Promise<ProductionOrder> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdProductionOrder,
    variables: { input: {
      _version: currentVersion, 
      id: orderId,
      countEditWorkCenter: count
    }}
  })
  return updateResult.data.updateDgtPdProductionOrder
}

export const updateCountEditOperationDirectly = async (orderId : string, currentVersion: string, count: number)
: Promise<ProductionOrder> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdProductionOrder,
    variables: { input: {
      _version: currentVersion, 
      id: orderId,
      countEditOperation: count
    }}
  })
  return updateResult.data.updateDgtPdProductionOrder
}

export const updateEaPerPack = async (orderId : string, currentVersion: number, eaPerPack: number)
: Promise<ProductionOrder> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdProductionOrder,
    variables: { input: {
      _version: currentVersion, 
      id: orderId,
      infoEaPerPack: eaPerPack
    }}
  })
  return updateResult.data.updateDgtPdProductionOrder
}

export const getOrderQueryForRetain = /* GraphQL */ `
  query GetOrderQueryForRetain($id: ID!) {
    getDgtPdProductionOrder(id: $id) {
      id
      orderQty
      orderType
      infoEaPerPack
      shelfLife
      _version
    }
  }
`;

export const getOrderQueryForFgInspectionTypes = /* GraphQL */ `
  query GetOrderQueryForFgInspectionTypes($id: ID!) {
    getDgtPdProductionOrder(id: $id) {
      id
      fgInspectionTypes
      _version
    }
  }
`;